import { AuthState } from '@/store/types';

// ===========================================================================================
// Member pricing
// ===========================================================================================

/**
 * Compare item's promos, if they match apply them
 *
 * @param {MenuItem | OrderItem} item
 * @param {CheckoutMemberInfo} memberInfo
 * @param {string} [arrayName='options'] - options by default, orderOptions can be passed for order items
 * @param {boolean} [hasPricingOptions=false]
 * @return {void}
 */
export function compareMemberItem(item: MenuItem | OrderItem, memberInfo: CheckoutMemberInfo, arrayName: string = 'options', hasPricingOptions: boolean = false): void {
	// If the item is an alcoholic item, we do not apply any discount
	if(!item.alcoholic_beverage) {

		// If we are treating cart items, we do not want to treat promos if the item has a pricing option & item does indeed have promos
		if(!hasPricingOptions && item.promos) {
			let tempPromoInfo: TemporaryPromoInfo = {
				promoApplied: false,
				itemAdjustedPrice: Number(item.price)
			};

			// Loop through the item/member promos
			item.promos.forEach((promo: string) => {
				tempPromoInfo = compareMemberPromosAndDiscountPrice(promo, memberInfo, tempPromoInfo.itemAdjustedPrice, tempPromoInfo.promoApplied);
			});

			// Assign the adjusted price to the item
			item.adjustedPrice = tempPromoInfo.promoApplied ? tempPromoInfo.itemAdjustedPrice.toFixed(2) : undefined;
		}

		// Options
		compareMemberOptions(item, memberInfo, arrayName, hasPricingOptions);
	}
}

/**
 * Compare options and sub options promos, if they match apply them
 *
 * @param {MenuItem | OrderItem | Option} item
 * @param {CheckoutMemberInfo} memberInfo
 * @param {string} [arrayName='options'] - options by default, orderOptions can be passed for order items
 * @param {boolean} [hasPricingOptions=false]
 * @return {void}
 */
export function compareMemberOptions(item: MenuItem | OrderItem | Option, memberInfo: CheckoutMemberInfo, arrayName: string = 'options', hasPricingOptions: boolean = false): void {
	if(item[arrayName]) {
		item[arrayName].forEach((optionGroup: OptionGroup | OrderOptionGroup) => {
			if(optionGroup.values) {
				optionGroup.values.forEach((option: Option | OrderOption) => {
					if(option.promos) {
						let tempPromoInfo: TemporaryPromoInfo = {
							promoApplied: false,
							itemAdjustedPrice: Number(option.price)
						};

						// Loop through the option/member promos
						option.promos.forEach((promo: string) => {
							tempPromoInfo = compareMemberPromosAndDiscountPrice(promo, memberInfo, tempPromoInfo.itemAdjustedPrice, tempPromoInfo.promoApplied);
						});

						// Assign the adjusted price to the option
						option.adjustedPrice = tempPromoInfo.promoApplied ? tempPromoInfo.itemAdjustedPrice.toFixed(2) : undefined;

						// If we are treating cart items and the order option is of pricing type, we have to also modify
						// the item's price with the pricing adjusted price.
						if(hasPricingOptions && (optionGroup as OrderOptionGroup).optionGroupType === 'pricing') {
							item.adjustedPrice = option.adjustedPrice;
						}
					}

					// Recursivity - compare sub options
					if(option.options) {
						compareMemberOptions(option as Option, memberInfo);
					}
				});
			}
		});
	}
}

/**
 * Loop through the member's promo array and compare them with the item's promo.
 * If they are the same then we discount the price with a percentage or a dollar
 * amount. We then return the price to add to the temp price.
 * Reminder: for a promo to work, the id has to be the same and we then use the object information
 * from the member's promo array to get the percent/amount
 *
 * @param {string} itemPromo
 * @param {CheckoutMemberInfo} memberInfo
 * @param {number} itemAdjustedPrice
 * @param {boolean} promoApplied
 * @return {{ promoApplied: boolean, itemAdjustedPrice: number }}
 */
export function compareMemberPromosAndDiscountPrice(itemPromo: string, memberInfo: CheckoutMemberInfo, itemAdjustedPrice: number, promoApplied: boolean): { promoApplied: boolean, itemAdjustedPrice: number } {
	// Loop through the member promos to check if they compare to the item's one
	memberInfo.promos.forEach((memberPromo: MemberPromo) => {

		// If the item contains the promo the member has we create an adjustedPrice for that item
		if(itemPromo === memberPromo.id) {
			memberInfo.promosApplied.add(memberPromo.id);
			promoApplied = true;

			// Percentage
			if(memberPromo.percent) {
				itemAdjustedPrice = Math.max(0, Number(itemAdjustedPrice) - (Number(itemAdjustedPrice) * (memberPromo.percent / 100)));
			}

			// Dollar
			else {
				itemAdjustedPrice = Math.max(0, Number(itemAdjustedPrice) - memberPromo.amount);
			}
		}
	});
	return { promoApplied, itemAdjustedPrice };
}

// ===========================================================================================
// Subsidy pricing
// ===========================================================================================

/**
 * Get eligible subsidies for the current user based on target_users setting
 * (Currently, there should never be more than one subsidy per restaurant)
 *
 * @param {Subsidy[]} subsidies
 * @param {AuthState} authState
 * @return {Subsidy[]}
 */
export function getEligibleSubsidies(subsidies: Subsidy[], authState: AuthState): Subsidy[] {
	if (!subsidies || !Array.isArray(subsidies)) {
		return [];
	}

	return subsidies.filter(s =>
		s.is_active && (
			s.target_users === 'all' ||
			// If target_users is 'codes', check if the user is authenticated and has a subsidy code
			// We don't validate the subsidy code here since this is for visual pricing only
			(s.target_users === 'codes' && authState.authenticated && !!authState.user?.subsidyCode)
		)
	);
}

/**
 * Check if an item is eligible for a specific subsidy based on target_items
 *
 * @param {MenuItem | OrderItem} item
 * @param {Subsidy} subsidy
 * @return {boolean}
 */
export function isItemEligibleForSubsidy(item: MenuItem | OrderItem, subsidy: Subsidy): boolean {
	return subsidy.target_items === 'all' || (subsidy.target_items === 'meals' && item.meal_package === true);
}

/**
 * Compare item for subsidy eligibility and apply discount
 *
 * @param {MenuItem | OrderItem} item
 * @param {Subsidy[]} eligibleSubsidies
 * @param {string} [arrayName='options']
 * @param {boolean} [hasPricingOptions=false]
 * @param {string} [memberProvider]
 * @return {void}
 */
export function compareSubsidyItem(item: MenuItem | OrderItem, eligibleSubsidies: Subsidy[], arrayName: string = 'options', hasPricingOptions: boolean = false, memberProvider?: string): void {
	// Don't apply subsidies if member pricing is available (mutual exclusivity - for now)
	if (memberProvider === 'ELB' || memberProvider === 'SPH') {
		return;
	}

	// If the item is an alcoholic item, we do not apply any subsidies
	if(!item.alcoholic_beverage) {

		// If we are treating cart items, we do not want to treat subsidies if the item has a pricing option
		if(!hasPricingOptions) {
			// Apply subsidy pricing if eligible
			let tempSubsidyInfo: TemporaryPromoInfo = {
				promoApplied: false,
				itemAdjustedPrice: Number(item.price)
			};

			eligibleSubsidies.forEach((subsidy: Subsidy) => {
				// Check if this item is eligible for the subsidy based on target_items
				if (isItemEligibleForSubsidy(item, subsidy)) {
					tempSubsidyInfo = applySubsidyDiscount(subsidy, tempSubsidyInfo.itemAdjustedPrice, tempSubsidyInfo.promoApplied);
				}
			});

			// Assign the subsidy price to the item as adjustedPrice, or clear it if no subsidies applied
			if (tempSubsidyInfo.promoApplied) {
				item.adjustedPrice = tempSubsidyInfo.itemAdjustedPrice.toFixed(2);
			}
			else {
				// Clear adjustedPrice if no subsidies are applied (revert to original price)
				item.adjustedPrice = undefined;
			}
		}

		// Options
		compareSubsidyOptions(item, eligibleSubsidies, arrayName, hasPricingOptions);
	}
}

/**
 * Compare options for subsidy eligibility
 *
 * @param {MenuItem | OrderItem | Option} item
 * @param {Subsidy[]} eligibleSubsidies
 * @param {string} [arrayName='options']
 * @param {boolean} [hasPricingOptions=false]
 * @return {void}
 */
export function compareSubsidyOptions(item: MenuItem | OrderItem, eligibleSubsidies: Subsidy[], arrayName: string = 'options', hasPricingOptions: boolean = false): void {
	if(item[arrayName]) {
		item[arrayName].forEach((optionGroup: OptionGroup | OrderOptionGroup) => {
			if(optionGroup.values) {
				optionGroup.values.forEach((option: Option | OrderOption) => {
					// Subsidy discount only applies to pricing-type options, NOT to addon-type options
					const isPricingOption = (optionGroup as OrderOptionGroup).optionGroupType === 'pricing' || (optionGroup as OptionGroup).type === 'pricing';

					if (isPricingOption) {
						let tempSubsidyInfo: TemporaryPromoInfo = {
							promoApplied: false,
							itemAdjustedPrice: Number(option.price)
						};

						eligibleSubsidies.forEach((subsidy: Subsidy) => {
							// For pricing options, inherit eligibility from the parent item (this is because options do not have the meal_package property - for now)
							if (isItemEligibleForSubsidy(item, subsidy)) {
								tempSubsidyInfo = applySubsidyDiscount(subsidy, tempSubsidyInfo.itemAdjustedPrice, tempSubsidyInfo.promoApplied);
							}
						});

						// Assign the calculated subsidy discount as adjustedPrice, or clear it if no subsidies applied
						if (tempSubsidyInfo.promoApplied) {
							option.adjustedPrice = tempSubsidyInfo.itemAdjustedPrice.toFixed(2);

							// If we are treating cart items and the order option is of pricing type, we have to also modify
							// the item's adjustedPrice with the option's adjustedPrice.
							if(hasPricingOptions && isPricingOption) {
								item.adjustedPrice = option.adjustedPrice;
							}
						}
						else {
							// Clear adjustedPrice if no subsidies are applied (revert to original price)
							option.adjustedPrice = undefined;

							// If we are treating cart items and the order option is of pricing type, also clear the item's adjustedPrice
							if(hasPricingOptions && isPricingOption) {
								item.adjustedPrice = undefined;
							}
						}
					}
				});
			}
		});
	}
}

/**
 * Apply subsidy discount to an item's price
 *
 * @param {Subsidy} subsidy
 * @param {number} currentPrice
 * @param {boolean} promoApplied
 * @return {TemporaryPromoInfo}
 */
export function applySubsidyDiscount(subsidy: Subsidy, currentPrice: number, promoApplied: boolean): TemporaryPromoInfo {
	let itemAdjustedPrice = currentPrice;

	if(subsidy.type === 'discount') {
		// Apply percentage discount
		if(subsidy.discount_percentage) {
			const percentage = Math.max(0, +subsidy.discount_percentage);
			itemAdjustedPrice = Math.max(0, currentPrice - (currentPrice * (percentage / 100)));
			promoApplied = true;
		}

		// Apply dollar amount discount
		else if(subsidy.discount_amount) {
			const discountAmount = Math.max(0, +subsidy.discount_amount);
			itemAdjustedPrice = Math.max(0, currentPrice - discountAmount);
			promoApplied = true;
		}
	}

	// Apply fixed price
	else if(subsidy.type === 'fixed_price' && subsidy.discount_fixed_price) {
		itemAdjustedPrice = Math.max(0, +subsidy.discount_fixed_price);
		promoApplied = true;
	}

	return { promoApplied, itemAdjustedPrice };
}

/**
 * Apply subsidy pricing to rotational menus
 *
 * @param {RotationalMenu[]} rotationalMenus
 * @param {Subsidy[]} eligibleSubsidies
 * @param {string} [memberProvider]
 * @return {void}
 */
export function applySubsidiesToRotationalMenus(rotationalMenus: RotationalMenu[], eligibleSubsidies: Subsidy[], memberProvider?: string): void {
	rotationalMenus.forEach((rotationalMenu: RotationalMenu) => {
		rotationalMenu.menus.forEach((menu: Menu) => {
			if(menu.items) {
				menu.items.forEach((item: MenuItem) => {
					compareSubsidyItem(item, eligibleSubsidies, 'options', false, memberProvider);
				});
			}
			if (menu.sections?.length) {
				menu.sections.forEach((section: MenuSection) => {
					section.items.forEach((item: MenuItem) => {
						compareSubsidyItem(item, eligibleSubsidies, 'options', false, memberProvider);
					});
				});
			}
		});
	});
}